import * as THREE from "three";
import "./styles.css";
import { AssetRuntime, loadAssetManifest, type MaterialAssetLease, type StaticAssetLease } from "./assets";
import { AmbientAudioSystem } from "./audio/AmbientAudioSystem";
import { DEFAULT_SETTINGS, HOME_POSITION, WORLD_RULES_VERSION, type ExperienceSettings, type FrameContext, type WorldSave } from "./core/types";
import { hasTouchInput } from "./core/device";
import { EnvironmentSystem } from "./environment";
import { AmbientLifeSystem } from "./life/AmbientLifeSystem";
import { NaturalInteractionFeedback, PlayerController, type InteractionRay, type InteractionTarget } from "./player";
import { createDefaultSave, hasWorldSave, loadWorldSave, resolveInitialSeed, saveWorld, shareSeedUrl } from "./storage/save";
import { ExperienceUI, TouchControls, type StartRequest } from "./ui";
import {
  ArchitectureLibrary,
  WorldSystem,
  type WorldArchitectureKind,
  type WorldVegetationKind,
  type WorldVisualAssets,
} from "./world";

interface InteractiveData {
  object: THREE.Object3D;
  kind: "gate" | "bench";
}

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("Application root was not found.");

let renderer: THREE.WebGLRenderer;
try {
  renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance" });
} catch {
  app.innerHTML = '<div class="boot-placeholder" role="alert">此浏览器无法启动 WebGL2。请开启硬件加速后重试。</div>';
  throw new Error("WebGL2 initialization failed.");
}

renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.75));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.domElement.setAttribute("aria-label", "江南水乡实时三维场景");
app.append(renderer.domElement);

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(DEFAULT_SETTINGS.comfort.fov, window.innerWidth / window.innerHeight, 0.08, 720);
camera.position.fromArray(HOME_POSITION);
const clock = new THREE.Clock();
const raycaster = new THREE.Raycaster();
const environment = new EnvironmentSystem(scene);
const isTouchDevice = hasTouchInput();
const WORLD_LOAD_RADIUS = 2;
let activeSeed = resolveInitialSeed();
const hasInitialSave = hasWorldSave(activeSeed);
let activeSave = hasInitialSave ? loadWorldSave(activeSeed) : createDefaultSave(activeSeed);
if (isTouchDevice && !hasInitialSave) {
  activeSave.settings.quality = "medium";
  activeSave.settings.comfort.headBob = false;
}
let settings: ExperienceSettings = structuredClone(activeSave.settings);
let world: WorldSystem;
let player: PlayerController<InteractiveData>;
let life: AmbientLifeSystem;
let audio: AmbientAudioSystem;
let naturalFeedback: NaturalInteractionFeedback;
let assets: AssetRuntime;
let terrainLeases: MaterialAssetLease[] = [];
let architectureLeases: MaterialAssetLease[] = [];
let architecture: ArchitectureLibrary;
let architectureReady: Promise<ArchitectureLibrary>;
let worldVisuals: WorldVisualAssets;
let saveInterval = 0;
let disposed = false;
let touchControls: TouchControls;

const ui = new ExperienceUI({
  root: app,
  settings,
  initialSeed: activeSeed,
  save: hasWorldSave(activeSeed) ? { seed: activeSeed, updatedAt: activeSave.updatedAt } : null,
  callbacks: {
    onStart: (request) => startExperience(request),
    onResume: async () => {
      void audio.resume();
      const pointerLocked = await player.resume();
      if (pointerLocked && isTouchDevice) touchControls.show();
      else window.setTimeout(() => ui.showPause(), 0);
    },
    onReturnHome: () => {
      player.returnHome();
      persist();
    },
    onSettingsChange: (next) => {
      settings = structuredClone(next);
      camera.fov = settings.comfort.fov;
      camera.updateProjectionMatrix();
      resize();
      persist();
    },
    onReload: () => location.reload(),
  },
});

function createWorldRuntime(seed: string, save: WorldSave): void {
  world?.dispose();
  life?.dispose();
  player?.dispose();
  naturalFeedback?.dispose();
  audio?.dispose();

  world = new WorldSystem(scene, {
    seed,
    version: WORLD_RULES_VERSION,
    loadRadius: WORLD_LOAD_RADIUS,
    waterMaterial: environment.waterMaterial,
    visuals: worldVisuals,
  });
  life = new AmbientLifeSystem(scene, world, seed);
  audio = new AmbientAudioSystem(world, seed);
  player = new PlayerController<InteractiveData>({
    camera,
    domElement: renderer.domElement,
    world,
    homePosition: HOME_POSITION,
    onPausedChange: (paused) => {
      if (paused) {
        touchControls?.hide();
        ui.showPause();
      } else {
        ui.hidePause();
        if (isTouchDevice) touchControls?.show();
      }
    },
    interaction: {
      resolveTarget: resolveInteraction,
      onTargetChange: (target) => {
        ui.setInteractionHint(target?.label ?? null);
        touchControls?.setInteractionAvailable(target !== null);
      },
      onInteract: handleInteraction,
    },
  });
  player.restore(save.player);
  naturalFeedback = new NaturalInteractionFeedback({
    scene,
    camera,
    world,
    onCue: (cue) => audio.playNaturalFeedback(cue),
    reducedMotion: matchMedia("(prefers-reduced-motion: reduce)").matches,
  });
}

async function startExperience(request: StartRequest): Promise<void> {
  await runtimeReady;
  const nextSave = request.mode === "load" ? loadWorldSave(request.seed) : createDefaultSave(request.seed);
  activeSeed = nextSave.seed;
  activeSave = nextSave;
  settings = structuredClone(nextSave.settings);
  ui.setSettings(settings);
  camera.fov = settings.comfort.fov;
  camera.updateProjectionMatrix();
  history.replaceState(null, "", shareSeedUrl(activeSeed));
  createWorldRuntime(activeSeed, nextSave);
  void audio.resume();
  const pointerLocked = await player.resume();
  if (pointerLocked && isTouchDevice) touchControls.show();
  else window.setTimeout(() => ui.showPause(), 0);
  persist();
}

function resolveInteraction(ray: InteractionRay): InteractionTarget<InteractiveData> | null {
  raycaster.set(ray.origin, ray.direction);
  raycaster.far = ray.maxDistance;
  const hits = raycaster.intersectObjects(world.getInteractiveObjects(), true);
  for (const hit of hits) {
    let object: THREE.Object3D | null = hit.object;
    while (object && !object.userData.interaction) object = object.parent;
    const interaction = object?.userData.interaction as { id: string; label: string; kind: "gate" | "bench" } | undefined;
    if (object && interaction) {
      return { id: interaction.id, label: interaction.label, distance: hit.distance, data: { object, kind: interaction.kind } };
    }
  }
  return null;
}

function handleInteraction(target: InteractionTarget<InteractiveData>): void {
  if (!target.data) return;
  if (target.data.kind === "gate") {
    const state = target.data.object.userData.interaction as { open: boolean; label: string; closedRotation?: number };
    state.open = !state.open;
    state.label = state.open ? "合上院门" : "推开院门";
    const closedRotation = state.closedRotation ?? 0;
    target.data.object.rotation.y = state.open ? closedRotation - Math.PI * 0.48 : closedRotation;
    world.setInteractionCollisionEnabled(target.data.object, !state.open);
  }
  if (target.data.kind === "bench") {
    const anchor = target.data.object.getObjectByName("BenchSitAnchor");
    if (!anchor) return;
    const position = anchor.getWorldPosition(new THREE.Vector3());
    player.sitAt(position.x, position.z);
  }
}

function persist(): void {
  if (!player) return;
  activeSave = {
    version: 2,
    seed: activeSeed,
    updatedAt: Date.now(),
    player: player.snapshot(),
    settings: structuredClone(settings),
  };
  saveWorld(activeSave);
}

function resize(): void {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, settings.quality === "high" ? 1.75 : 1.25));
  renderer.setSize(window.innerWidth, window.innerHeight);
}

function animate(): void {
  if (disposed) return;
  requestAnimationFrame(animate);
  const delta = Math.min(clock.getDelta(), 0.1);
  const frame: FrameContext = {
    delta,
    elapsed: clock.elapsedTime,
    camera,
    renderer,
    settings,
    paused: player.isPaused,
  };
  environment.update(frame);
  world.update(frame);
  life.update(frame);
  player.update(frame);
  naturalFeedback.update(frame);
  audio.update(frame);
  saveInterval += delta;
  if (saveInterval >= 5) {
    saveInterval = 0;
    persist();
  }
  renderer.render(scene, camera);
}

function dispose(event?: PageTransitionEvent): void {
  if (event?.persisted) {
    persist();
    return;
  }
  if (disposed) return;
  disposed = true;
  persist();
  ui.dispose();
  touchControls?.dispose();
  player?.dispose();
  naturalFeedback?.dispose();
  audio?.dispose();
  life?.dispose();
  world?.dispose();
  for (const lease of terrainLeases) lease.release();
  terrainLeases = [];
  architecture?.dispose();
  for (const lease of architectureLeases) lease.release();
  architectureLeases = [];
  assets?.dispose();
  environment.dispose();
  renderer.dispose();
}

const runtimeReady = prepareWorldAssets().then(() => createWorldRuntime(activeSeed, activeSave));
void runtimeReady.then(animate).catch((error: unknown) => {
  ui.showWebGLError(error instanceof Error ? error.message : "写实场景资源载入失败，请重新加载后再试。");
});
touchControls = new TouchControls(app, {
  onMove: (input) => player.setTouchMovement(input),
  onLook: (deltaX, deltaY) => player.addTouchLook(deltaX, deltaY),
  onInteract: () => player.interact(),
  onCrouchChange: (crouching) => player.setTouchCrouching(crouching),
  onPause: () => player.pause(),
});
ui.showStart();
window.addEventListener("resize", resize);
window.addEventListener("pagehide", dispose);
renderer.domElement.addEventListener("webglcontextlost", (event) => {
  event.preventDefault();
  player?.pause();
  ui.showWebGLError("图形设备暂时中断。请重新加载场景后继续散步。");
});
document.addEventListener("visibilitychange", () => {
  if (document.hidden) persist();
});
resize();

async function prepareWorldAssets(): Promise<void> {
  const manifest = await loadAssetManifest();
  assets = new AssetRuntime(manifest);
  const [stone, earth, grass] = await Promise.all([
    assets.acquireMaterial("stone-pavers"),
    assets.acquireMaterial("brown-mud"),
    assets.acquireMaterial("grass-path"),
  ]);
  terrainLeases = [stone, earth, grass];
  worldVisuals = {
    terrainMaterials: [stone.material, earth.material, grass.material, earth.material],
    acquireStatic: (kind, variant) => acquireVegetation(kind, variant),
    acquireArchitecture: (kind) => acquireArchitecture(kind),
  };
  architectureReady = prepareArchitecture(stone.material);
}

const vegetationAssetIds: Record<WorldVegetationKind, string> = {
  grass: "grass-bermuda-01",
  fern: "fern-02",
  shrub: "shrub-03",
  cypress: "cypress-tree",
};
async function acquireVegetation(kind: WorldVegetationKind, variant: number): Promise<StaticAssetLease> {
  const sourceLease = await assets.acquireStatic(vegetationAssetIds[kind]);
  const children = [...sourceLease.object.children];
  if (children.length === 0) {
    sourceLease.release();
    throw new Error(`植被资源 ${vegetationAssetIds[kind]} 不包含可渲染变体。`);
  }
  const object = new THREE.Group();
  if (kind === "cypress") {
    for (const child of children) object.add(child);
  } else {
    const index = variant % children.length;
    const selected = children[index]!;
    selected.position.x = 0;
    selected.position.z = 0;
    object.add(selected);
  }
  object.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    child.castShadow = kind !== "grass";
    child.receiveShadow = true;
  });
  return {
    object,
    release: () => {
      object.removeFromParent();
      sourceLease.release();
    },
  };
}

async function prepareArchitecture(pavers: THREE.MeshStandardMaterial): Promise<ArchitectureLibrary> {
  const [stone, wood] = await Promise.all([
    assets.acquireMaterial("stone-wall"),
    assets.acquireMaterial("rough-wood"),
  ]);
  if (disposed) {
    stone.release();
    wood.release();
    throw new Error("Architecture loading was interrupted");
  }
  architectureLeases = [stone, wood];
  architecture = new ArchitectureLibrary({
    pavers,
    stone: stone.material,
    wood: wood.material,
    water: environment.waterMaterial,
  });
  world?.resetAround(camera.position);
  return architecture;
}

async function acquireArchitecture(kind: WorldArchitectureKind): Promise<StaticAssetLease> {
  return (await architectureReady).acquire(kind);
}
